import React, { useState, useEffect } from 'react';
import { Link, useLocation, useNavigate } from 'react-router-dom';
import { Button, Input, InputGroup, FormGroup, Form, Row, Col, Table } from 'reactstrap';
import { Translate, translate, getSortState, JhiPagination, JhiItemCount } from 'react-jhipster';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';

import { APP_DATE_FORMAT, APP_LOCAL_DATE_FORMAT } from 'app/config/constants';
import { ASC, DESC, ITEMS_PER_PAGE, SORT } from 'app/shared/util/pagination.constants';
import { overridePaginationStateWithQueryParams } from 'app/shared/util/entity-utils';
import { useAppDispatch, useAppSelector } from 'app/config/store';

import { IServiceRecord } from 'app/shared/model/service-record.model';
import { searchEntities, getEntities } from './service-record.reducer';

export const ServiceRecord = () => {
  const dispatch = useAppDispatch();

  const location = useLocation();
  const navigate = useNavigate();

  const [search, setSearch] = useState('');
  const [paginationState, setPaginationState] = useState(
    overridePaginationStateWithQueryParams(getSortState(location, ITEMS_PER_PAGE, 'id'), location.search)
  );

  const serviceRecordList = useAppSelector(state => state.serviceRecord.entities);
  const loading = useAppSelector(state => state.serviceRecord.loading);
  const totalItems = useAppSelector(state => state.serviceRecord.totalItems);

  const getAllEntities = () => {
    if (search) {
      dispatch(
        searchEntities({
          query: search,
          page: paginationState.activePage - 1,
          size: paginationState.itemsPerPage,
          sort: `${paginationState.sort},${paginationState.order}`,
        })
      );
    } else {
      dispatch(
        getEntities({
          page: paginationState.activePage - 1,
          size: paginationState.itemsPerPage,
          sort: `${paginationState.sort},${paginationState.order}`,
        })
      );
    }
  };

  const startSearching = e => {
    if (search) {
      setPaginationState({
        ...paginationState,
        activePage: 1,
      });
      dispatch(
        searchEntities({
          query: search,
          page: paginationState.activePage - 1,
          size: paginationState.itemsPerPage,
          sort: `${paginationState.sort},${paginationState.order}`,
        })
      );
    }
    e.preventDefault();
  };

  const clear = () => {
    setSearch('');
    setPaginationState({
      ...paginationState,
      activePage: 1,
    });
    dispatch(getEntities({}));
  };

  const handleSearch = event => setSearch(event.target.value);

  const sortEntities = () => {
    getAllEntities();
    const endURL = `?page=${paginationState.activePage}&sort=${paginationState.sort},${paginationState.order}`;
    if (location.search !== endURL) {
      navigate(`${location.pathname}${endURL}`);
    }
  };

  useEffect(() => {
    sortEntities();
  }, [paginationState.activePage, paginationState.order, paginationState.sort, search]);

  useEffect(() => {
    const params = new URLSearchParams(location.search);
    const page = params.get('page');
    const sort = params.get(SORT);
    if (page && sort) {
      const sortSplit = sort.split(',');
      setPaginationState({
        ...paginationState,
        activePage: +page,
        sort: sortSplit[0],
        order: sortSplit[1],
      });
    }
  }, [location.search]);

  const sort = p => () => {
    setPaginationState({
      ...paginationState,
      order: paginationState.order === ASC ? DESC : ASC,
      sort: p,
    });
  };

  const handlePagination = currentPage =>
    setPaginationState({
      ...paginationState,
      activePage: currentPage,
    });

  const handleSyncList = () => {
    sortEntities();
  };

  return (
    <div>
      <h2 id="service-record-heading" data-cy="ServiceRecordHeading">
        <Translate contentKey="oodoExcelCreatorApp.serviceRecord.home.title">Service Records</Translate>
        <div className="d-flex justify-content-end">
          <Button className="me-2 " color="primary" tag={Link} to={`/service-record/upload`} disabled={loading}>
            <FontAwesomeIcon icon="upload" /> <Translate contentKey="oodoExcelCreatorApp.erpaxa.home.upload">Upload Master Data</Translate>
          </Button>
          <Button className="me-2" color="info" onClick={handleSyncList} disabled={loading}>
            <FontAwesomeIcon icon="sync" spin={loading} />{' '}
            <Translate contentKey="oodoExcelCreatorApp.serviceRecord.home.refreshListLabel">Refresh List</Translate>
          </Button>
          <Link to="/service-record/new" className="btn btn-primary jh-create-entity" id="jh-create-entity" data-cy="entityCreateButton">
            <FontAwesomeIcon icon="plus" />
            &nbsp;
            <Translate contentKey="oodoExcelCreatorApp.serviceRecord.home.createLabel">Create new Service Record</Translate>
          </Link>
        </div>
      </h2>
      <Row>
        <Col sm="12">
          <Form onSubmit={startSearching}>
            <FormGroup>
              <InputGroup>
                <Input
                  type="text"
                  name="search"
                  defaultValue={search}
                  onChange={handleSearch}
                  placeholder={translate('oodoExcelCreatorApp.serviceRecord.home.search')}
                />
                <Button className="input-group-addon">
                  <FontAwesomeIcon icon="search" />
                </Button>
                <Button type="reset" className="input-group-addon" onClick={clear}>
                  <FontAwesomeIcon icon="trash" />
                </Button>
              </InputGroup>
            </FormGroup>
          </Form>
        </Col>
      </Row>
      <div className="table-responsive">
        {serviceRecordList && serviceRecordList.length > 0 ? (
          <Table responsive>
            <thead>
              <tr>
                <th className="hand" onClick={sort('id')}>
                  <Translate contentKey="oodoExcelCreatorApp.serviceRecord.id">ID</Translate> <FontAwesomeIcon icon="sort" />
                </th>
                <th className="hand" onClick={sort('name')}>
                  <Translate contentKey="oodoExcelCreatorApp.serviceRecord.name">Name</Translate> <FontAwesomeIcon icon="sort" />
                </th>
                <th className="hand" onClick={sort('requestedDate')}>
                  <Translate contentKey="oodoExcelCreatorApp.serviceRecord.requestedDate">Requested Date</Translate>{' '}
                  <FontAwesomeIcon icon="sort" />
                </th>
                <th className="hand" onClick={sort('createDate')}>
                  <Translate contentKey="oodoExcelCreatorApp.serviceRecord.createDate">Create Date</Translate>{' '}
                  <FontAwesomeIcon icon="sort" />
                </th>
                <th className="hand" onClick={sort('customerId')}>
                  <Translate contentKey="oodoExcelCreatorApp.serviceRecord.customerId">Customer Id</Translate>{' '}
                  <FontAwesomeIcon icon="sort" />
                </th>
                <th className="hand" onClick={sort('sequenceId')}>
                  <Translate contentKey="oodoExcelCreatorApp.serviceRecord.sequenceId">Sequence Id</Translate>{' '}
                  <FontAwesomeIcon icon="sort" />
                </th>
                <th className="hand" onClick={sort('membershipNum')}>
                  <Translate contentKey="oodoExcelCreatorApp.serviceRecord.membershipNum">Membership Num</Translate>{' '}
                  <FontAwesomeIcon icon="sort" />
                </th>
                <th className="hand" onClick={sort('memberId')}>
                  <Translate contentKey="oodoExcelCreatorApp.serviceRecord.memberId">Member Id</Translate> <FontAwesomeIcon icon="sort" />
                </th>
                <th className="hand" onClick={sort('memberContactNo')}>
                  <Translate contentKey="oodoExcelCreatorApp.serviceRecord.memberContactNo">Member Contact No</Translate>{' '}
                  <FontAwesomeIcon icon="sort" />
                </th>
                <th className="hand" onClick={sort('policyNo')}>
                  <Translate contentKey="oodoExcelCreatorApp.serviceRecord.policyNo">Policy No</Translate> <FontAwesomeIcon icon="sort" />
                </th>
                <th className="hand" onClick={sort('memberType')}>
                  <Translate contentKey="oodoExcelCreatorApp.serviceRecord.memberType">Member Type</Translate>{' '}
                  <FontAwesomeIcon icon="sort" />
                </th>
                <th className="hand" onClick={sort('type')}>
                  <Translate contentKey="oodoExcelCreatorApp.serviceRecord.type">Type</Translate> <FontAwesomeIcon icon="sort" />
                </th>
                <th className="hand" onClick={sort('vehicleType')}>
                  <Translate contentKey="oodoExcelCreatorApp.serviceRecord.vehicleType">Vehicle Type</Translate>{' '}
                  <FontAwesomeIcon icon="sort" />
                </th>
                <th className="hand" onClick={sort('vehiclePlate')}>
                  <Translate contentKey="oodoExcelCreatorApp.serviceRecord.vehiclePlate">Vehicle Plate</Translate>{' '}
                  <FontAwesomeIcon icon="sort" />
                </th>
                <th className="hand" onClick={sort('productId')}>
                  <Translate contentKey="oodoExcelCreatorApp.serviceRecord.productId">Product Id</Translate> <FontAwesomeIcon icon="sort" />
                </th>
                <th className="hand" onClick={sort('providerId')}>
                  <Translate contentKey="oodoExcelCreatorApp.serviceRecord.providerId">Provider Id</Translate>{' '}
                  <FontAwesomeIcon icon="sort" />
                </th>
                <th className="hand" onClick={sort('driverName')}>
                  <Translate contentKey="oodoExcelCreatorApp.serviceRecord.driverName">Driver Name</Translate>{' '}
                  <FontAwesomeIcon icon="sort" />
                </th>
                <th className="hand" onClick={sort('driverNum')}>
                  <Translate contentKey="oodoExcelCreatorApp.serviceRecord.driverNum">Driver Num</Translate> <FontAwesomeIcon icon="sort" />
                </th>
                <th className="hand" onClick={sort('fromLocation')}>
                  <Translate contentKey="oodoExcelCreatorApp.serviceRecord.fromLocation">From Location</Translate>{' '}
                  <FontAwesomeIcon icon="sort" />
                </th>
                <th className="hand" onClick={sort('toLocation')}>
                  <Translate contentKey="oodoExcelCreatorApp.serviceRecord.toLocation">To Location</Translate>{' '}
                  <FontAwesomeIcon icon="sort" />
                </th>
                <th className="hand" onClick={sort('state')}>
                  <Translate contentKey="oodoExcelCreatorApp.serviceRecord.state">State</Translate> <FontAwesomeIcon icon="sort" />
                </th>
                <th className="hand" onClick={sort('isImported')}>
                  <Translate contentKey="oodoExcelCreatorApp.serviceRecord.isImported">Is Imported</Translate>{' '}
                  <FontAwesomeIcon icon="sort" />
                </th>
                <th className="hand" onClick={sort('creditProformaNumber')}>
                  <Translate contentKey="oodoExcelCreatorApp.serviceRecord.creditProformaNumber">Credit Proforma Number</Translate>{' '}
                  <FontAwesomeIcon icon="sort" />
                </th>
                <th className="hand" onClick={sort('routeRate')}>
                  <Translate contentKey="oodoExcelCreatorApp.serviceRecord.routeRate">Route Rate</Translate> <FontAwesomeIcon icon="sort" />
                </th>
                <th className="hand" onClick={sort('vendorRating')}>
                  <Translate contentKey="oodoExcelCreatorApp.serviceRecord.vendorRating">Vendor Rating</Translate>{' '}
                  <FontAwesomeIcon icon="sort" />
                </th>
                <th className="hand" onClick={sort('dateTimeFrom')}>
                  <Translate contentKey="oodoExcelCreatorApp.serviceRecord.dateTimeFrom">Date Time From</Translate>{' '}
                  <FontAwesomeIcon icon="sort" />
                </th>
                <th className="hand" onClick={sort('dateTimeTo')}>
                  <Translate contentKey="oodoExcelCreatorApp.serviceRecord.dateTimeTo">Date Time To</Translate>{' '}
                  <FontAwesomeIcon icon="sort" />
                </th>
                <th className="hand" onClick={sort('comments')}>
                  <Translate contentKey="oodoExcelCreatorApp.serviceRecord.comments">Comments</Translate> <FontAwesomeIcon icon="sort" />
                </th>
                <th className="hand" onClick={sort('idPopulated')}>
                  <Translate contentKey="oodoExcelCreatorApp.serviceRecord.idPopulated">Id Populated</Translate>{' '}
                  <FontAwesomeIcon icon="sort" />
                </th>
                <th className="hand" onClick={sort('dataProcessed')}>
                  <Translate contentKey="oodoExcelCreatorApp.serviceRecord.dataProcessed">Data Processed</Translate>{' '}
                  <FontAwesomeIcon icon="sort" />
                </th>
                <th />
              </tr>
            </thead>
            <tbody>
              {serviceRecordList.map((serviceRecord, i) => (
                <tr key={`entity-${i}`} data-cy="entityTable">
                  <td>
                    <Button tag={Link} to={`/service-record/${serviceRecord.id}`} color="link" size="sm">
                      {serviceRecord.id}
                    </Button>
                  </td>
                  <td>{serviceRecord.name}</td>
                  <td>{serviceRecord.requestedDate}</td>
                  <td>{serviceRecord.createDate}</td>
                  <td>{serviceRecord.customerId}</td>
                  <td>{serviceRecord.sequenceId}</td>
                  <td>{serviceRecord.membershipNum}</td>
                  <td>{serviceRecord.memberId}</td>
                  <td>{serviceRecord.memberContactNo}</td>
                  <td>{serviceRecord.policyNo}</td>
                  <td>{serviceRecord.memberType}</td>
                  <td>{serviceRecord.type}</td>
                  <td>{serviceRecord.vehicleType}</td>
                  <td>{serviceRecord.vehiclePlate}</td>
                  <td>{serviceRecord.productId}</td>
                  <td>{serviceRecord.providerId}</td>
                  <td>{serviceRecord.driverName}</td>
                  <td>{serviceRecord.driverNum}</td>
                  <td>{serviceRecord.fromLocation}</td>
                  <td>{serviceRecord.toLocation}</td>
                  <td>{serviceRecord.state}</td>
                  <td>{serviceRecord.isImported ? 'true' : 'false'}</td>
                  <td>{serviceRecord.creditProformaNumber}</td>
                  <td>{serviceRecord.routeRate}</td>
                  <td>{serviceRecord.vendorRating}</td>
                  <td>{serviceRecord.dateTimeFrom}</td>
                  <td>{serviceRecord.dateTimeTo}</td>
                  <td>{serviceRecord.comments}</td>
                  <td>{serviceRecord.idPopulated ? 'true' : 'false'}</td>
                  <td>{serviceRecord.dataProcessed ? 'true' : 'false'}</td>
                  <td className="text-end">
                    <div className="btn-group flex-btn-group-container">
                      <Button tag={Link} to={`/service-record/${serviceRecord.id}`} color="info" size="sm" data-cy="entityDetailsButton">
                        <FontAwesomeIcon icon="eye" />{' '}
                        <span className="d-none d-md-inline">
                          <Translate contentKey="entity.action.view">View</Translate>
                        </span>
                      </Button>
                      <Button
                        tag={Link}
                        to={`/service-record/${serviceRecord.id}/edit?page=${paginationState.activePage}&sort=${paginationState.sort},${paginationState.order}`}
                        color="primary"
                        size="sm"
                        data-cy="entityEditButton"
                      >
                        <FontAwesomeIcon icon="pencil-alt" />{' '}
                        <span className="d-none d-md-inline">
                          <Translate contentKey="entity.action.edit">Edit</Translate>
                        </span>
                      </Button>
                      <Button
                        tag={Link}
                        to={`/service-record/${serviceRecord.id}/delete?page=${paginationState.activePage}&sort=${paginationState.sort},${paginationState.order}`}
                        color="danger"
                        size="sm"
                        data-cy="entityDeleteButton"
                      >
                        <FontAwesomeIcon icon="trash" />{' '}
                        <span className="d-none d-md-inline">
                          <Translate contentKey="entity.action.delete">Delete</Translate>
                        </span>
                      </Button>
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </Table>
        ) : (
          !loading && (
            <div className="alert alert-warning">
              <Translate contentKey="oodoExcelCreatorApp.serviceRecord.home.notFound">No Service Records found</Translate>
            </div>
          )
        )}
      </div>
      {totalItems ? (
        <div className={serviceRecordList && serviceRecordList.length > 0 ? '' : 'd-none'}>
          <div className="justify-content-center d-flex">
            <JhiItemCount page={paginationState.activePage} total={totalItems} itemsPerPage={paginationState.itemsPerPage} i18nEnabled />
          </div>
          <div className="justify-content-center d-flex">
            <JhiPagination
              activePage={paginationState.activePage}
              onSelect={handlePagination}
              maxButtons={5}
              itemsPerPage={paginationState.itemsPerPage}
              totalItems={totalItems}
            />
          </div>
        </div>
      ) : (
        ''
      )}
    </div>
  );
};

export default ServiceRecord;
